import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import authService from "../services/authService";
import deviceTrustService from "../services/deviceTrustService";
import { AuthContext } from "../context/AuthContext";
import Captcha from "../components/Captcha";
import { Form, Button, Alert, Container } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import { motion, AnimatePresence } from "framer-motion";
import SettingsService from "../services/SettingsService";
import { API_BASE_URL } from "../configs/apiConfig";
import Spinner from "../components/Spinner";
import "../assets/styles/LoginPage.css";
import messageService from "../services/messageService";

const LoginPage = () => {
    const { login } = useContext(AuthContext);
    const navigate = useNavigate();
    const captchaRef = useRef(null);
    const [pageSettings, setPageSettings] = useState({
        logo: "",
        headerText: null,
        mainText: null,
        footerText: null,
        backgroundImage: "",
        externalLinks: []
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isCustomBackgroundLoaded, setIsCustomBackgroundLoaded] = useState(false);
    const [isSettingsLoaded, setIsSettingsLoaded] = useState(false);

    const [credentials, setCredentials] = useState({
        usernameOrEmail: "",
        password: "",
        captchaKey: "",
        captchaValue: "",
        trustDevice: false,
    });

    const [error, setError] = useState(null);
    const [warning, setWarning] = useState(null);
    const [loading, setLoading] = useState(false);
    const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
    const [twoFactorCode, setTwoFactorCode] = useState("");
    const [userId, setUserId] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [twoFactorAttempted, setTwoFactorAttempted] = useState(false);
    const [resendCounter, setResendCounter] = useState(0);
    const [resendDisabled, setResendDisabled] = useState(false);
    const [alternateAuth, setAlternateAuth] = useState(false);
    const [currentTab, setCurrentTab] = useState('login');
    const [anonymousMessage, setAnonymousMessage] = useState({ 
        subject: "", 
        content: "",
        captchaKey: "",
        captchaValue: ""
    });
    const [anonymousSuccess, setAnonymousSuccess] = useState(null);
    const [anonymousLoading, setAnonymousLoading] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const anonymousCaptchaRef = useRef(null);

    useEffect(() => {
        const initializePage = async () => {
            try {
                const [settingsResponse, captchaResponse] = await Promise.all([
                    SettingsService.getSetting("login"),
                    authService.getCaptcha()
                ]);

                if (settingsResponse?.value) {
                    const settings = JSON.parse(settingsResponse.value);
                    
                    // Update all settings except background
                    setPageSettings(prev => ({
                        ...prev,
                        logo: settings.Logo ? `${API_BASE_URL}${encodeURI(settings.Logo)}` : "",
                        headerText: settings.HeaderText || "Welcome Header Text",
                        mainText: settings.MainText || "Welcome Main Text",
                        footerText: settings.FooterText || "Welcome Footer Text",
                        externalLinks: Array.isArray(settings.ExternalLinks) 
                            ? settings.ExternalLinks.map(link => ({
                                name: link.Name || "",
                                url: link.Url || "",
                                icon: link.Icon ? `${API_BASE_URL}${encodeURI(link.Icon)}` : ""
                            })).slice(0, 4)
                            : []
                    }));

                    // Handle custom background if exists
                    if (settings.BackgroundImage) {
                        const customBg = `${API_BASE_URL}${encodeURI(settings.BackgroundImage)}`;
                        const img = new Image();
                        img.onload = () => {
                            setPageSettings(prev => ({
                                ...prev,
                                backgroundImage: customBg
                            }));
                            setIsCustomBackgroundLoaded(true);
                        };
                        img.src = customBg;
                    }
                } else {
                    // If no settings, use default texts
                    setPageSettings(prev => ({
                        ...prev,
                        headerText: "Welcome Header Text",
                        mainText: "Welcome Main Text",
                        footerText: "Welcome Footer Text"
                    }));
                    setIsCustomBackgroundLoaded(true);
                }

                // Handle captcha
                if (captchaResponse) {
                    setCredentials(prev => ({
                        ...prev,
                        captchaKey: captchaResponse.captchaKey,
                        captchaValue: "",
                    }));
                }
            } catch (error) {
                console.error('Error loading settings:', error);
                // On error, use default texts
                setPageSettings(prev => ({
                    ...prev,
                    headerText: "Welcome Header Text",
                    mainText: "Welcome Main Text",
                    footerText: "Welcome Footer Text"
                }));
                setIsCustomBackgroundLoaded(true);
            } finally {
                setIsSettingsLoaded(true);
            }
        };

        initializePage();
    }, []);

    useEffect(() => {
        let timer;
        if (resendCounter > 0) {
            timer = setTimeout(() => {
                setResendCounter(resendCounter - 1);
            }, 1000);
        } else {
            setResendDisabled(false);
        }
        return () => clearTimeout(timer);
    }, [resendCounter]);

    const fetchCaptcha = async () => {
        setLoading(true);
        try {
            if (captchaRef.current) {
                captchaRef.current.refreshCaptcha();
            } else {
                const response = await authService.getCaptcha();
                setCredentials(prev => ({
                    ...prev,
                    captchaKey: response.captchaKey,
                    captchaValue: "",
                }));
            }
        } catch (err) {
            setError("Failed to load CAPTCHA.");
        } finally {
            setLoading(false);
        }
    };
    
    const handleLogin = async (e) => {
        e.preventDefault();
        setError(null);
        setWarning(null);
        setLoading(true);
    
        try {
            const isTrusted = !deviceTrustService.shouldShowCaptcha();
            const deviceId = deviceTrustService.getDeviceId();

            const loginCredentials = {
                usernameOrEmail: credentials.usernameOrEmail,
                password: credentials.password,
                deviceId,
                trustDevice: credentials.trustDevice,
                bypassCaptcha: isTrusted,
                captchaKey: isTrusted ? "bypass" : credentials.captchaKey,
                captchaValue: isTrusted ? "bypass" : credentials.captchaValue
            };

            const response = await login(loginCredentials);
    
            if (response.requiresTwoFactor) {
                setRequiresTwoFactor(true);
                setUserId(response.userId);
                setTwoFactorAttempted(false);
                setLoading(false);
                return;
            }
    
            if (response.userType !== undefined) {
                if (credentials.trustDevice) {
                    deviceTrustService.setSuccessfulLogin();
                }
                handleRedirection(response.userType);
            } else {
                setError("Authentication failed.");
                if (!isTrusted) {
                    await fetchCaptcha();
                }
            }
        } catch (err) {
            if (err.response?.status === 401) {
                setError("Invalid username or password.");
            } else {
                const errorData = err.response?.data;
                let errorMessage;
                
                // Handle validation errors
                if (errorData?.errors) {
                    // Extract first validation error message from any field
                    const firstError = Object.values(errorData.errors)[0];
                    if (Array.isArray(firstError) && firstError.length > 0) {
                        errorMessage = firstError[0];
                    } else {
                        errorMessage = errorData.title || "Validation error occurred.";
                    }
                } else if (typeof errorData === 'string') {
                    errorMessage = errorData;
                } else if (errorData?.message) {
                    errorMessage = errorData.message;
                } else if (errorData?.title) {
                    errorMessage = errorData.title;
                } else {
                    errorMessage = "Invalid username, password, or CAPTCHA.";
                }
                
                if (errorMessage.includes("SMTP Error") || errorMessage.includes("Failed to initiate 2FA")) {
                    setWarning("Unable to send verification code. Please contact support or try an alternate method.");
                    setAlternateAuth(true);
                    setRequiresTwoFactor(true);
                    setUserId(err.response?.userId || null);
                } else {
                    setError(errorMessage);
                }
            }
            
            if (deviceTrustService.shouldShowCaptcha()) {
                await fetchCaptcha();
            }
        } finally {
            setLoading(false);
        }
    };
    
    const handleVerify2FA = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
    
        try {
            const response = await authService.verifyTwoFactor(twoFactorCode);
    
            if (response.userType !== undefined) {
                if (credentials.trustDevice) {
                    deviceTrustService.setSuccessfulLogin();
                } else {
                    deviceTrustService.removeTrust();
                }
                handleRedirection(response.userType);
            } else {
                setError("2FA verification failed.");
                setTwoFactorAttempted(true);
            }
        } catch (err) {
            setError("Invalid or expired 2FA code. Please try again.");
            setTwoFactorCode("");
            setTwoFactorAttempted(true);
        } finally {
            setLoading(false);
        }
    };
    
    const handleResend2FA = async () => {
        setLoading(true);
        setError(null);
        
        try {
            await authService.resend2FACode();
            setResendCounter(15);
            setResendDisabled(true);
            alert("A new verification code has been sent.");
        } catch (err) {
            if (err.response?.data?.includes("SMTP Error")) {
                setError("Failed to send verification code. Email service is currently unavailable.");
            } else {
                setError("Failed to resend verification code. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };
    
    const handleAlternateAuth = async () => {
        alert("Please contact support for assistance with account access.");
    };
    
    const handleRedirection = (userType) => {
        const type = String(userType);
        
        switch (type) {
            case "0":
                navigate("/admin-dashboard", { replace: true });
                break;
            case "1":
            case "2":
                navigate("/user-dashboard", { replace: true });
                break;
            default:
                setError("Invalid user type received");
                break;
        }
    };

    const handleAnonymousSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setAnonymousSuccess(null);
        setAnonymousLoading(true);

        try {
            if (!anonymousMessage.content.trim()) {
                throw new Error('Message content is required');
            }

            if (!anonymousMessage.captchaValue) {
                throw new Error('Please complete the captcha');
            }

            // Validate captcha first
            try {
                await authService.validateCaptcha(anonymousMessage.captchaKey, anonymousMessage.captchaValue);
            } catch (err) {
                if (anonymousCaptchaRef.current) {
                    anonymousCaptchaRef.current.refreshCaptcha();
                }
                throw new Error('Invalid captcha. Please try again.');
            }

            // If captcha is valid, proceed with sending the message
            await messageService.createAnonymous(anonymousMessage, attachments);
            setAnonymousSuccess('Message sent successfully');
            setAnonymousMessage({ 
                subject: '', 
                content: '', 
                captchaKey: '',
                captchaValue: ''
            });
            setAttachments([]);
            if (anonymousCaptchaRef.current) {
                anonymousCaptchaRef.current.refreshCaptcha();
            }
        } catch (err) {
            setError(err.message || 'Failed to send message');
            if (anonymousCaptchaRef.current) {
                anonymousCaptchaRef.current.refreshCaptcha();
            }
        } finally {
            setAnonymousLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setAttachments(prevAttachments => [...prevAttachments, ...files]);
    };

    const removeAttachment = (index) => {
        setAttachments(prevAttachments => prevAttachments.filter((_, i) => i !== index));
    };

    const getFormattedDate = () => {
        const date = new Date();
        const dayNum = date.getDate();
        // Add leading zero for single-digit days
        const day = dayNum < 10 ? `0${dayNum}` : `${dayNum}`;
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return { day, month, year };
    };

    const { day, month, year } = getFormattedDate();

    const renderUnknownMessage = () => (
        <>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="glass-card main-card"
            >
                <div className="date-display">
                    <div className="date-number">{day}</div>
                    <div className="date-text">
                        {month} <span className="year-text">{year}</span>
                    </div>
                </div>

                <div className="login-content">
                    <motion.h2
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                    >
                        {pageSettings.headerText}
                    </motion.h2>
                    
                    <motion.p 
                        className="main-text"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                    >
                        {pageSettings.mainText}
                    </motion.p>
                    
                    <motion.p 
                        className="footer-text"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.4 }}
                    >
                        {pageSettings.footerText}
                    </motion.p>
                </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="glass-card login-info-card"
            >
                <h3 className="form-title">Write Here Your Unknown Message</h3>
                
                {error && <Alert variant="danger">{error}</Alert>}
                {anonymousSuccess && <Alert variant="success">{anonymousSuccess}</Alert>}
                
                <Form onSubmit={handleAnonymousSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Control
                            type="text"
                            placeholder="Subject (optional)"
                            value={anonymousMessage.subject}
                            onChange={(e) => setAnonymousMessage(prev => ({ ...prev, subject: e.target.value }))}
                            className="glass-input"
                            disabled={anonymousLoading}
                        />
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Form.Control
                            as="textarea"
                            rows={5}
                            placeholder="My Message"
                            value={anonymousMessage.content}
                            onChange={(e) => setAnonymousMessage(prev => ({ ...prev, content: e.target.value }))}
                            className="glass-input message-input"
                            disabled={anonymousLoading}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <div className="d-flex align-items-center">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => document.getElementById('anonymousFileInput').click()}
                                disabled={anonymousLoading}
                                className="glass-input"
                            >
                                <i className="bi bi-paperclip"></i> Attach Files
                            </Button>
                            <input
                                type="file"
                                id="anonymousFileInput"
                                multiple
                                className="d-none"
                                onChange={handleFileChange}
                                disabled={anonymousLoading}
                            />
                        </div>
                        {attachments.length > 0 && (
                            <div className="attachment-list mt-2">
                                {attachments.map((file, index) => (
                                    <div key={index} className="attachment-item">
                                        <span>{file.name}</span>
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="text-danger"
                                            onClick={() => removeAttachment(index)}
                                            disabled={anonymousLoading}
                                        >
                                            ×
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Captcha
                            ref={anonymousCaptchaRef}
                            onCaptchaChange={(key, value) => setAnonymousMessage(prev => ({
                                ...prev,
                                captchaKey: key,
                                captchaValue: value
                            }))}
                        />
                    </Form.Group>
                    
                    <Button 
                        type="submit" 
                        className="message-button" 
                        disabled={anonymousLoading}
                    >
                        {anonymousLoading ? <Spinner /> : "SEND MY MESSAGE ANONYMOUSLY"}
                    </Button>
                </Form>
            </motion.div>
        </>
    );

    const renderLoginContent = () => {
        // Don't render text content until settings are loaded
        if (!isSettingsLoaded) {
            return null;
        }

        return (
            <div className="login-content">
                <motion.h2
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                >
                    {pageSettings.headerText}
                </motion.h2>
                
                <motion.p 
                    className="main-text"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                >
                    {pageSettings.mainText}
                </motion.p>
                
                <motion.p 
                    className="footer-text"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                >
                    {pageSettings.footerText}
                </motion.p>
            </div>
        );
    };

    const renderLoginForm = () => (
        <AnimatePresence mode="wait">
            {!requiresTwoFactor ? (
                <motion.div
                    key="login"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="glass-card login-info-card"
                >
                    <h3 className="form-title">Login Info</h3>
                    
                    {error && <Alert variant="danger">{error}</Alert>}
                    
                    <Form onSubmit={handleLogin}>
                        <Form.Group className="mb-3">
                            <Form.Control
                                type="text"
                                value={credentials.usernameOrEmail}
                                onChange={(e) => setCredentials({
                                    ...credentials,
                                    usernameOrEmail: e.target.value,
                                })}
                                placeholder="Email or Personal Code"
                                required
                                className="glass-input"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4 password-input-group">
                            <div className="password-input-wrapper">
                                <Form.Control
                                    type={showPassword ? "text" : "password"}
                                    value={credentials.password}
                                    onChange={(e) => setCredentials({
                                        ...credentials,
                                        password: e.target.value,
                                    })}
                                    placeholder="Password"
                                    required
                                    className="glass-input"
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                                </button>
                            </div>
                        </Form.Group>

                        {deviceTrustService.shouldShowCaptcha() && (
                            <>
                                <Form.Group className="mb-4">
                                    <Captcha
                                        ref={captchaRef}
                                        onCaptchaChange={(key, value) => setCredentials(prev => ({
                                            ...prev,
                                            captchaKey: key,
                                            captchaValue: value
                                        }))}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <div className="trust-device-container">
                                        <Form.Check
                                            type="checkbox"
                                            id="trust-device"
                                            checked={credentials.trustDevice}
                                            onChange={(e) => setCredentials(prev => ({
                                                ...prev,
                                                trustDevice: e.target.checked
                                            }))}
                                            label="Trust this device and skip CAPTCHA for 30 days"
                                            className="trust-device"
                                        />
                                    </div>
                                </Form.Group>
                            </>
                        )}

                        <Button type="submit" className="login-button" disabled={loading}>
                            {loading ? <Spinner /> : "LOGIN"}
                        </Button>
                    </Form>
                </motion.div>
            ) : (
                <motion.div
                    key="2fa"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="glass-card login-info-card"
                >
                    <h3 className="form-title">Two-Factor Authentication</h3>
                    
                    {error && <Alert variant="danger">{error}</Alert>}
                    {warning && <Alert variant="warning">{warning}</Alert>}

                    <Form onSubmit={handleVerify2FA}>
                        {!alternateAuth && (
                            <Form.Group className="mb-4">
                                <Form.Control
                                    type="text"
                                    placeholder="Enter verification code"
                                    value={twoFactorCode}
                                    onChange={(e) => setTwoFactorCode(e.target.value)}
                                    required
                                    className="glass-input"
                                />
                            </Form.Group>
                        )}

                        {alternateAuth ? (
                            <Button 
                                className="verify-button alternate" 
                                variant="warning"
                                onClick={() => {
                                    setRequiresTwoFactor(false);
                                    setTwoFactorCode("");
                                    setAlternateAuth(false);
                                    setWarning(null);
                                    fetchCaptcha();
                                }}
                            >
                                Back to Login
                            </Button>
                        ) : (
                            <>
                                <Button type="submit" className="verify-button" disabled={loading}>
                                    {loading ? <Spinner /> : "Verify Code"}
                                </Button>

                                {twoFactorAttempted && (
                                    <Button
                                        variant="link"
                                        className="resend-button"
                                        onClick={handleResend2FA}
                                        disabled={loading || resendDisabled}
                                    >
                                        Resend Code
                                        {resendCounter > 0 && ` (${resendCounter}s)`}
                                    </Button>
                                )}
                            </>
                        )}
                    </Form>
                </motion.div>
            )}
        </AnimatePresence>
    );

    return (
        <div className="login-page" style={{
            backgroundImage: pageSettings.backgroundImage ? `url(${pageSettings.backgroundImage})` : 'none',
            backgroundColor: pageSettings.backgroundImage ? 'transparent' : '#1a1a2e',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            transition: 'background-image 0.3s ease-in-out'
        }}>
            <div className="login-nav-container">
                <div className="login-top-nav">
                    <button 
                        className={`login-nav-button ${currentTab === 'login' ? 'active' : ''}`}
                        onClick={() => setCurrentTab('login')}
                    >
                        Login
                    </button>
                    <button 
                        className={`login-nav-button ${currentTab === 'unknown' ? 'active' : ''}`}
                        onClick={() => setCurrentTab('unknown')}
                    >
                        Unknown Message
                    </button>
                </div>
            </div>
            
            <Container className="login-container">
                <AnimatePresence mode="wait">
                    {currentTab === 'login' ? (
                        <>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className="glass-card main-card"
                            >
                                <div className="date-display">
                                    <div className="date-number">{day}</div>
                                    <div className="date-text">
                                        {month} <span className="year-text">{year}</span>
                                    </div>
                                </div>

                                {renderLoginContent()}
                            </motion.div>
                            {renderLoginForm()}
                        </>
                    ) : renderUnknownMessage()}
                </AnimatePresence>

                <div className="footer-section">
                    <div className="external-links-container">
                        {pageSettings.externalLinks.slice(0, 2).map((link, index) => (
                            <motion.a 
                                key={`left-${link.url}-${index}`}
                                href={link.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="external-link"
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.6 + (index * 0.1) }}
                            >
                                {link.icon ? (
                                    <img src={link.icon} alt={link.name} />
                                ) : (
                                    <i className="bi bi-link-45deg"></i>
                                )}
                            </motion.a>
                        ))}
                    </div>

                    {pageSettings.logo && (
                        <motion.img 
                            src={pageSettings.logo} 
                            alt="Logo" 
                            className="footer-logo"
                            initial={{ scale: 0.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.4 }}
                            onClick={() => window.open('https://fec.ai', '_blank', 'noopener,noreferrer')}
                            style={{ cursor: 'pointer' }}
                        />
                    )}

                    <div className="external-links-container">
                        {pageSettings.externalLinks.slice(2, 4).map((link, index) => (
                            <motion.a 
                                key={`right-${link.url}-${index}`}
                                href={link.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="external-link"
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.8 + (index * 0.1) }}
                            >
                                {link.icon ? (
                                    <img src={link.icon} alt={link.name} />
                                ) : (
                                    <i className="bi bi-link-45deg"></i>
                                )}
                            </motion.a>
                        ))}
                    </div>
                </div>

                <motion.div 
                    className="copyright"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1 }}
                >
                    © 2025 FaraErtebat AI Corp®. (FEC.AI). All rights reserved. Unauthorized access or use is prohibited.
                </motion.div>
            </Container>
        </div>
    );
};

export default LoginPage;